import { t } from "@lingui/macro";
import { ComponentType } from "react";
import { Platform } from "react-native";

import {
  FaceIdIcon,
  FingerprintIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { IconProps } from "@whoosh/design-system/src/components/primitive";

import { BiometricUnlockType } from "./biometricUnlock";

export const getBiometricUnlockLabel = (
  platform: typeof Platform.OS,
  biometricUnlockType: BiometricUnlockType
): string => {
  if (platform === "ios" && biometricUnlockType === "face") {
    return t`Use Face ID`;
  }

  if (platform === "ios" && biometricUnlockType === "fingerprint") {
    return t`Use Touch ID`;
  }

  return t`Use biometrics`;
};

type BiometricSignInPromptCopy = {
  body: string;
  title: string;
};

export const getBiometricSignInPromptCopy = (
  platform: typeof Platform.OS,
  biometricUnlockType: BiometricUnlockType
): BiometricSignInPromptCopy => {
  if (platform === "ios" && biometricUnlockType === "face") {
    return {
      body: t`Use Face ID to securely sign back in to Whoosh.`,
      title: t`Sign back in with Face ID`,
    };
  }

  if (platform === "ios" && biometricUnlockType === "fingerprint") {
    return {
      body: t`Use Touch ID to securely sign back in to Whoosh.`,
      title: t`Sign back in with Touch ID`,
    };
  }

  return {
    body: t`Use biometrics to securely sign back in to Whoosh.`,
    title: t`Sign back in with biometrics`,
  };
};

type BiometricOptInCopy = {
  title: string;
  benefitCopy: string;
  primaryCta: string;
  toastTitle: string;
  toastDescription: string;
  Glyph: ComponentType<IconProps>;
};

export const getBiometricOptInCopy = (
  platform: typeof Platform.OS,
  enrolledTypes: BiometricUnlockType[]
): BiometricOptInCopy => {
  const hasFace = enrolledTypes.includes("face");
  const hasFingerprint = enrolledTypes.includes("fingerprint");

  if (platform === "ios" && hasFace) {
    return {
      title: t`Sign in with Face ID?`,
      benefitCopy: t`Use Face ID to sign in next time. Booking a tee time takes a couple of taps, with no password to type.`,
      primaryCta: t`Use Face ID`,
      toastTitle: t`Face ID is on`,
      toastDescription: t`Next time, look at your phone to sign in.`,
      Glyph: FaceIdIcon,
    };
  }

  if (platform === "ios" && hasFingerprint) {
    return {
      title: t`Sign in with Touch ID?`,
      benefitCopy: t`Use Touch ID to sign in next time. Booking a tee time takes a couple of taps, with no password to type.`,
      primaryCta: t`Use Touch ID`,
      toastTitle: t`Touch ID is on`,
      toastDescription: t`Next time, use your fingerprint to sign in.`,
      Glyph: FingerprintIcon,
    };
  }

  return {
    title: t`Sign in with biometrics?`,
    benefitCopy: t`Use biometrics to sign in next time. Booking a tee time takes a couple of taps, with no password to type.`,
    primaryCta: t`Use Biometrics`,
    toastTitle: t`Biometric sign-in is on`,
    toastDescription: t`Next time, use biometrics to sign in.`,
    Glyph: FingerprintIcon,
  };
};
